import type { FC } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import PrivateContent from 'app/common/PrivateContent';
import MatchStatusTag from 'app/components/MatchStatusTag';
import VideogameIcon from 'app/components/VideogameIcon';
import Tooltip from 'app/components/Tooltip';
import Icon from 'app/components/Icon';
import IncidentIndicator from 'app/components/IncidentIndicator';
import arrowLongIcon from 'app/icons/arrow_long.svg';
import fireIcon from 'app/icons/fire.svg';
import starIcon from 'app/icons/star_plain.svg';
import lightningIcon from 'app/icons/lightning_fill.svg';
import { isMatchNeedReviewed } from 'app/utils/match';
import PandascoreVideogameFeature from 'app/common/PandascoreVideogameFeature';
import BlacklistIndicator from 'app/components/BlacklistIndicator';

import GameAdvantageIndicator from './GameAdvantageIndicator';
import MatchPreviewTeams from './MatchPreviewTeams';
import MatchActionTrader from './MatchActionTrader';
import MatchActionBookmaker from './MatchActionBookmaker';
import MatchPreviewFooter from './MatchPreviewFooter';
import { getBeginTime, getBeginDate, shouldShowActions } from './utils';
import type { MatchPreviewData } from './types';
import style from './style.scss';

interface Props {
  match: MatchPreviewData;
  userType: UserType;
  updating: boolean;
  canDisplayUnsuspendNotification?: boolean;
  onSelectBlueprint: (id: number) => void;
  onSelectTrader: (id: number, type: 'live' | 'microbetting') => void;
  onUnassignTrader: (type: 'live' | 'microbetting') => void;
  onBook: () => void;
  onCreateMarkets: () => void;
}

const gameSlugClassNameMap: { [key in VideogameSlug]: string | undefined } = {
  'league-of-legends': style.infoContainer_lol,
  'cs-go': style.infoContainer_cs,
  'dota-2': style.infoContainer_dota,
  ow: style.infoContainer_ow,
  rl: style.infoContainer_rl,
  'cod-mw': style.infoContainer_cod,
  'r6-siege': style.infoContainer_r6,
  fifa: style.infoContainer_fifa,
  valorant: style.infoContainer_valorant,
  kog: style.infoContainer_kog,
  'lol-wild-rift': style.infoContainer_lolWildRift,
  'starcraft-2': style.infoContainer_starcraft,
  'starcraft-brood-war': style.infoContainer_starcraft,
  'e-soccer': style.infoContainer_eSoccer,
  'e-basketball': style.infoContainer_eBasketball,
  'e-cricket': style.infoContainer_eCricket,
  fortnite: style.infoContainer,
  pubg: style.infoContainer,
  mlbb: style.infoContainer_mlbb
};

const matchTypeAcronym: { [key in MatchType]: string } = {
  best_of: 'BO',
  all_games_played: 'AGP',
  first_to: 'FT',
  custom: 'C',
  ow_best_of: 'OBO',
  red_bull_home_ground: 'RBHG'
};

const matchTypeLabel: { [key in MatchType]: string } = {
  best_of: 'common-match_type-best_of',
  all_games_played: 'common-match_type-all_games_played',
  first_to: 'common-match_type-first_to',
  custom: 'common-match_type-custom',
  ow_best_of: 'common-match_type-ow_best_of',
  red_bull_home_ground: 'common-match_type-redbull-home-ground'
};

const MatchPreview: FC<Props> = ({
  match,
  userType,
  updating,
  canDisplayUnsuspendNotification = false,
  onSelectBlueprint,
  onSelectTrader,
  onUnassignTrader,
  onBook,
  onCreateMarkets
}) => {
  const [t] = useTranslation();
  const incident = match.incidents ? match.incidents[0] : undefined;
  const time = match.scheduled_at ? getBeginTime(match.scheduled_at) : undefined;
  const date = match.scheduled_at ? getBeginDate(match.scheduled_at) : undefined;
  const showActions = shouldShowActions(match);
  const infoContainerClassName = classnames(
    style.infoContainer,
    gameSlugClassNameMap[match.videogame.slug]
  );
  const teamWithAdvantage = match.game_advantage
    ? match.opponents.find(({ id }) => id === match.game_advantage)
    : undefined;
  const matchWithSuppremacy = !!match.traderInputs?.any_input_different_than_zero;

  return (
    <div className={style.container}>
      {isMatchNeedReviewed(match, userType) && (
        <div className={style.reviewAlert}>
          <Icon href={arrowLongIcon} className={style.reviewArrowIcon} />
          {t('events-match_need_review')}
        </div>
      )}
      {canDisplayUnsuspendNotification &&
        match.betting_metadata?.has_match_markets_to_unsuspend && (
          <div className={style.reviewAlert}>
            <Icon href={arrowLongIcon} className={style.reviewArrowIcon} />
            {t('events-match_need_unsuspend')}
          </div>
        )}
      <div className={infoContainerClassName}>
        <div className={style.main}>
          <div className={style.withLiveContainer}>
            {match.betting_metadata && match.betting_metadata.coverage === 'live_odds' && (
              <Tooltip
                className={style.liveContainer}
                content={t(
                  match.betting_metadata.micromarkets_enabled
                    ? 'coverage_live_odds_microbetting'
                    : 'coverage-live_odds'
                )}
              >
                <div className={style.liveIcons}>
                  <Icon href={fireIcon} className={style.liveIcon} />
                  {match.betting_metadata.micromarkets_enabled && (
                    <Icon href={lightningIcon} className={style.liveIcon} />
                  )}
                </div>
                <div className={style.liveIconLabel}>{t('coverage-live_odds')}</div>
              </Tooltip>
            )}
            {match.betting_metadata && match.betting_metadata.coverage === 'next_game_live' && (
              <div className={style.liveContainer}>
                <div className={style.liveIcons}>
                  <Icon href={starIcon} className={style.liveIcon} />
                </div>
                <div className={style.liveIconLabel}>{t('coverage-next_game_live')}</div>
              </div>
            )}
          </div>
          <VideogameIcon slug={match.videogame.slug} size={20} />
          <div>
            <div className={style.dateContainer}>
              <div className={style.time}>{time}</div>
              <div className={style.date}>{date}</div>
            </div>
          </div>
          <Tooltip
            className={style.matchTypeTag}
            content={
              <span className={style.matchTypeLabel}>{t(matchTypeLabel[match.match_type])}</span>
            }
          >
            {matchTypeAcronym[match.match_type]}{' '}
            {match.match_type !== 'red_bull_home_ground' && match.number_of_games}
          </Tooltip>
          <div className={style.statusTagContainer}>
            <MatchStatusTag
              status={match.status}
              liveCheckedIn={
                match.status === 'pre_match' &&
                match.betting_metadata?.coverage === 'live_odds' &&
                !!match.betting_metadata?.live_checked_in
              }
              className={style.matchStatus}
            />
            {teamWithAdvantage && <GameAdvantageIndicator opponent={teamWithAdvantage} />}
          </div>
          <div className={style.suppremacyContainer}>
            {matchWithSuppremacy && (
              <Tooltip content={t('events-match_ajusted_odds')}>
                <div className={style.suppremacyIndicator}>%</div>
              </Tooltip>
            )}
          </div>
          <MatchPreviewTeams match={match} />
          <div
            className={classnames(style.actionContainer, {
              [style.actionContainerLeft]: !showActions && !incident
            })}
          >
            {incident && (
              <IncidentIndicator
                incidents={match.incidents ?? []}
                alignment="middle"
                className={style.incident}
              />
            )}
            <BlacklistIndicator
              blacklistedPlayers={match.blacklistedPlayers}
              matchStatus={match.status}
            />
            <div className={style.actionContainerRelative}>
              <PandascoreVideogameFeature videogame={match.videogame.slug}>
                {showActions && (
                  <>
                    <PrivateContent feature="TRADER_INPUT">
                      <MatchActionTrader
                        match={match}
                        updating={updating}
                        onSelectBlueprint={onSelectBlueprint}
                        onBook={onBook}
                        onCreateMarkets={onCreateMarkets}
                      />
                    </PrivateContent>
                    <PrivateContent access={['bookmaker']}>
                      <MatchActionBookmaker
                        match={match}
                        updating={updating}
                        onSelectBlueprint={onSelectBlueprint}
                        onBook={onBook}
                      />
                    </PrivateContent>
                  </>
                )}
              </PandascoreVideogameFeature>
            </div>
          </div>
        </div>
        <MatchPreviewFooter
          userType={userType}
          match={match}
          updating={updating}
          onSelectBlueprint={onSelectBlueprint}
          onSelectTrader={onSelectTrader}
          onUnassignTrader={onUnassignTrader}
        />
      </div>
    </div>
  );
};

MatchPreview.displayName = 'MatchPreview';

export default MatchPreview;
export { gameSlugClassNameMap };
